import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import MuiButton from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Pagination from '@mui/material/Pagination';
import UploadIcon from '@mui/icons-material/Upload';

export interface InputNumberProps {
  value?: number | string | null;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
  formatter?: (value: any) => string;
  parser?: (displayValue: string | undefined) => number;
  precision?: number;
  [key: string]: any;
}

export const InputNumber: React.FC<InputNumberProps> = ({
  value,
  onChange,
  min,
  max,
  step,
  disabled,
  style,
  className,
  placeholder,
  formatter,
  parser,
}) => {
  const display =
    formatter && value != null ? formatter(value as number) : value != null ? String(value) : '';

  return (
    <TextField
      type="number"
      size="small"
      fullWidth
      className={className}
      disabled={disabled}
      placeholder={placeholder}
      value={display}
      sx={style}
      inputProps={{ min, max, step }}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onChange?.(null);
          return;
        }
        const n = parser ? parser(raw) : parseFloat(raw);
        onChange?.(Number.isNaN(n) ? null : n);
      }}
    />
  );
};

export interface AutoCompleteProps {
  value?: string;
  options?: { value: string; label?: React.ReactNode; [key: string]: any }[];
  onSelect?: (value: string, option: any) => void;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  filterOption?: boolean | ((inputValue: string, option?: any) => boolean);
  [key: string]: any;
}

export const AutoComplete: React.FC<AutoCompleteProps> = ({
  value,
  options = [],
  onSelect,
  onChange,
  style,
  className,
  children,
  disabled,
  placeholder,
  filterOption,
}) => {
  const childInput = children
    ? (React.Children.only(children) as React.ReactElement)
    : null
  const childPlaceholder = childInput?.props?.placeholder
  const inputValue = childInput?.props?.value ?? value ?? ''

  const filterOpts =
    filterOption === false
      ? (opts: typeof options) => opts
      : typeof filterOption === 'function'
        ? (opts: typeof options, state: { inputValue: string }) =>
            opts.filter((o) => filterOption(state.inputValue, o))
        : undefined

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      className={className}
      options={options}
      getOptionLabel={(opt) =>
        typeof opt === 'string'
          ? opt
          : opt.label != null
            ? String(opt.label)
            : String(opt.value)
      }
      filterOptions={filterOpts}
      inputValue={String(inputValue ?? '')}
      onInputChange={(_, v, reason) => {
        if (reason === 'reset') return
        onChange?.(v)
        childInput?.props?.onChange?.({ target: { value: v } })
      }}
      onChange={(_, opt) => {
        if (opt == null) {
          onChange?.('')
          onSelect?.('', null)
          return
        }
        if (typeof opt === 'string') {
          onChange?.(opt)
          onSelect?.(opt, { value: opt })
          return
        }
        onChange?.(String(opt.value))
        onSelect?.(opt.value, opt)
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder || childPlaceholder || ''}
          disabled={disabled}
          fullWidth
          sx={style}
        />
      )}
      sx={{ width: '100%', ...((style as object) || {}) }}
    />
  )
}

export type CheckboxChangeEvent = React.ChangeEvent<HTMLInputElement>;

export interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (e: { target: { checked: boolean } }) => void;
  disabled?: boolean;
  children?: React.ReactNode;
  indeterminate?: boolean;
  [key: string]: any;
}

export const AntCheckbox: React.FC<CheckboxProps> = ({
  checked,
  defaultChecked,
  onChange,
  disabled,
  children,
  indeterminate,
}) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        indeterminate={indeterminate}
        onChange={(e) => onChange?.({ target: { checked: e.target.checked } })}
      />
    }
    label={children}
  />
);

const CheckboxGroup: React.FC<{ value?: any[]; onChange?: (v: any[]) => void; children?: React.ReactNode }> = ({
  value = [],
  onChange,
  children,
}) => (
  <Box>
    {React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      const optVal = child.props.value;
      return React.cloneElement(child as React.ReactElement<any>, {
        checked: value.includes(optVal),
        onChange: () => {
          const next = value.includes(optVal) ? value.filter((v) => v !== optVal) : [...value, optVal];
          onChange?.(next);
        },
      });
    })}
  </Box>
);

export type AntCheckboxComponent = typeof AntCheckbox & { Group: typeof CheckboxGroup };

(AntCheckbox as AntCheckboxComponent).Group = CheckboxGroup;

export interface RadioProps {
  value?: any;
  checked?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  onChange?: (e: { target: { value: any } }) => void;
  [key: string]: any;
}

const RadioButton: React.FC<RadioProps> = ({ value, checked, disabled, children, ...rest }) => {
  void rest;
  return (
    <FormControlLabel
      value={value}
      control={<Radio disabled={disabled} checked={checked} size="small" />}
      label={children}
      sx={{ m: 0, border: '1px solid #d9d9d9', px: 1, mr: '-1px' }}
    />
  );
};

const AntRadioBase: React.FC<RadioProps> = ({ value, checked, disabled, children }) => (
  <FormControlLabel value={value} control={<Radio disabled={disabled} checked={checked} />} label={children} />
);
AntRadioBase.displayName = 'AntRadio';

const RadioGroupWrap: React.FC<{ value?: any; onChange?: (e: { target: { value: any } }) => void; children?: React.ReactNode; [key: string]: any }> = ({
  value,
  onChange,
  children,
  ...rest
}) => {
  void rest;
  return (
    <RadioGroup value={value} onChange={(e) => onChange?.({ target: { value: e.target.value } })}>
      {children}
    </RadioGroup>
  );
};

export type AntRadioComponent = typeof AntRadioBase & {
  Group: typeof RadioGroupWrap;
  Button: typeof RadioButton;
};

export const AntRadio = AntRadioBase as AntRadioComponent;
AntRadio.Group = RadioGroupWrap;
AntRadio.Button = RadioButton;

export interface CascaderProps {
  options?: any[];
  value?: (string | number)[];
  onChange?: (value: (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  changeOnSelect?: boolean;
  [key: string]: any;
}

type CascaderOption = {
  value: string | number;
  label?: string;
  children?: CascaderOption[];
};

/** Cascader theo cấp — luôn 4 cột; ô đầu ~1/3, 3 ô còn lại chia đều phần còn lại. */
const CASCADER_LEVEL_COUNT = 4

export const Cascader: React.FC<CascaderProps> = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Chọn',
  disabled,
  style,
  changeOnSelect = false,
}) => {
  const path = Array.isArray(value) ? value.filter((v) => v != null && v !== '') : [];

  const levels = React.useMemo(() => {
    const rows: {
      level: number;
      options: CascaderOption[];
      selected: CascaderOption | null;
      locked: boolean;
    }[] = [];
    let levelOptions: CascaderOption[] = options as CascaderOption[];
    for (let level = 0; level < CASCADER_LEVEL_COUNT; level += 1) {
      const pathKey = path[level];
      const locked = level > 0 && path[level - 1] == null;
      const selected =
        !locked && pathKey != null
          ? levelOptions.find((o) => String(o.value) === String(pathKey)) ?? null
          : null;
      rows.push({
        level,
        options: locked ? [] : levelOptions,
        selected,
        locked,
      });
      levelOptions = selected?.children?.length ? selected.children : [];
    }
    return rows;
  }, [options, path]);

  const onSelectLevel = (level: number, next: CascaderOption | null) => {
    if (!next) {
      onChange?.(path.slice(0, level));
      return;
    }
    const nextPath = [...path.slice(0, level), next.value];
    onChange?.(nextPath);
    void changeOnSelect;
  };

  const levelPlaceholders = [
    placeholder,
    'Chọn cấp 2…',
    'Chọn cấp 3…',
    'Chọn cấp 4…',
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        // Ô đầu 1/3; 3 ô sau chia đều 2/3 còn lại → 3:2:2:2
        gridTemplateColumns: {
          xs: '1fr',
          sm: '3fr 2fr 2fr 2fr',
        },
        gap: 1,
        width: '100%',
        minWidth: 0,
        ...((style as object) || {}),
      }}
    >
      {levels.map(({ level, options: levelOptions, selected, locked }) => (
        <Autocomplete
          key={level}
          size="small"
          disabled={disabled || locked}
          options={levelOptions}
          value={selected}
          getOptionLabel={(opt) => opt?.label ?? String(opt?.value ?? '')}
          isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
          onChange={(_, next) => onSelectLevel(level, next)}
          sx={{ minWidth: 0, width: '100%' }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={levelPlaceholders[level] || 'Chọn tiếp…'}
            />
          )}
        />
      ))}
    </Box>
  );
};

export interface UploadProps {
  fileList?: any[];
  beforeUpload?: (file: File) => boolean | Promise<boolean>;
  onChange?: (info: { file: any; fileList: any[] }) => void;
  listType?: 'text' | 'picture' | 'picture-card';
  action?: string;
  headers?: Record<string, string>;
  method?: string;
  accept?: string;
  multiple?: boolean;
  name?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  showUploadList?: boolean;
  [key: string]: any;
}

export const Upload: React.FC<UploadProps> & { Dragger: typeof UploadDragger } = ({
  fileList,
  beforeUpload,
  onChange,
  action,
  headers,
  method = 'post',
  accept,
  multiple,
  name = 'file',
  children,
  disabled,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [innerList, setInnerList] = useState<any[]>(fileList ?? []);

  React.useEffect(() => {
    if (fileList) setInnerList(fileList);
  }, [fileList]);

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    for (const file of arr) {
      if (beforeUpload) {
        const ok = await beforeUpload(file);
        if (!ok) continue;
      }
      const entry: any = { uid: `${Date.now()}-${file.name}`, name: file.name, status: 'uploading', originFileObj: file };
      const nextList = [...innerList, entry];
      setInnerList(nextList);
      onChange?.({ file: entry, fileList: nextList });

      if (action) {
        const fd = new FormData();
        fd.append(name, file);
        try {
          const res = await fetch(action, { method, headers, body: fd });
          const json = await res.json();
          entry.status = 'done';
          entry.response = json;
          const doneList = nextList.map((f) => (f.uid === entry.uid ? entry : f));
          setInnerList(doneList);
          onChange?.({ file: entry, fileList: doneList });
        } catch {
          entry.status = 'error';
          onChange?.({ file: entry, fileList: nextList });
        }
      }
    }
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept}
        multiple={multiple}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <Box
        onClick={() => !disabled && inputRef.current?.click()}
        sx={{ display: 'inline-block', cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        {children ?? (
          <MuiButton variant="outlined" startIcon={<UploadIcon />} disabled={disabled} sx={{ textTransform: 'none' }}>
            Upload
          </MuiButton>
        )}
      </Box>
      {innerList.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {innerList.map((f) => (
            <Typography key={f.uid} variant="body2">
              {f.name} {f.status === 'uploading' ? '…' : ''}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

const UploadDragger: React.FC<UploadProps> = (props) => (
  <Box
    sx={{
      border: '1px dashed #d9d9d9',
      borderRadius: 1,
      p: 3,
      textAlign: 'center',
      bgcolor: '#fafafa',
    }}
  >
    <Upload {...props} />
  </Box>
);

Upload.Dragger = UploadDragger;

export interface AntPaginationProps {
  current?: number;
  pageSize?: number;
  total?: number;
  onChange?: (page: number, pageSize?: number) => void;
  showSizeChanger?: boolean;
  pageSizeOptions?: (string | number)[];
  style?: React.CSSProperties;
  className?: string;
  size?: 'default' | 'small';
  showTotal?: (total: number, range: [number, number]) => React.ReactNode;
  [key: string]: any;
}

export const AntPagination: React.FC<AntPaginationProps> = ({
  current = 1,
  pageSize = 10,
  total = 0,
  onChange,
  style,
  className,
  showTotal,
}) => {
  const count = Math.max(1, Math.ceil(total / pageSize));
  const range: [number, number] = [(current - 1) * pageSize + 1, Math.min(current * pageSize, total)];
  return (
    <Box className={className} sx={{ display: 'flex', alignItems: 'center', gap: 2, ...style }}>
      {showTotal?.(total, range)}
      <Pagination count={count} page={current} onChange={(_, p) => onChange?.(p, pageSize)} color="primary" />
    </Box>
  );
};
